import { Box } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import { SText, TableLoader } from '@setvi/shared/components';
import { SInfiniteTable } from '@setvi/shared/components/s-table/infinite';
import type { Product } from '@setvi/shared/interfaces';
import { openProductAsset } from 'componentsV2/asset-preview';
import { isOutlookPath } from 'pages/outlook/outlook-compose-provider';

import { ProductsEmptyState } from '../../../components';
import { ContentTitleContainer } from '../../product-info';
import { useAssociatedResourceColumns } from '../useAssociatedResourceColumns';
import { useProductAssets } from './hooks';
import { toResourceRow } from './utils/to-resource-row';
import type { ProductAssetRow } from './utils/to-resource-row';

interface ProductAssetsProps {
  product: Product;
}

/**
 * The Resources tab for product-assets companies (CFS): documents from
 * revops-api, rendered with the same columns as Setvi associated resources
 * (read-only: favorites / email act on Setvi resources only). A click opens
 * a new tab with the in-app preview; only a web page opens as itself, and the
 * Outlook catalog opens the public file.
 */
export const ProductAssets = ({ product }: ProductAssetsProps) => {
  const { documents, isLoading } = useProductAssets({ product });
  const { columns } = useAssociatedResourceColumns({ readOnly: true });
  // An Outlook add-in tab opens in the system browser, outside the add-in
  // session, so there the public file replaces the in-app preview page.
  const { pathname } = useLocation();
  const fileOnly = isOutlookPath(pathname);

  const rows = documents.map(toResourceRow);

  const content = () => {
    if (isLoading) return <TableLoader columns={columns} />;

    if (!rows.length)
      return <ProductsEmptyState title="No Resources Available" />;

    return (
      <SInfiniteTable
        newDesign
        stickyHeader={false}
        list={rows}
        columns={columns}
        keyGetter={(row: ProductAssetRow) => row.ResourceID}
        row={{
          onClick: (row: ProductAssetRow) =>
            openProductAsset({
              productId: product.sku,
              asset: row.asset,
              fileOnly
            })
        }}
      />
    );
  };

  return (
    <Box flex={1} display="flex" flexDirection="column" width="100%">
      <ContentTitleContainer mb={2}>
        <SText title="Resources" weight="semibold" />
      </ContentTitleContainer>
      {content()}
    </Box>
  );
};
